import { useEffect } from 'react';
import { useApp } from '@/lib/core/providers/appProvider';
import { getFirstName, ImageMap, formatShortDate } from '@/lib/core/utils';
import { ImageWithCrops } from '@/services/platform/api/model';
import { useClientTranslation } from '@/lib/core/i18n';
import { useUIStore } from '@/lib/core/stores/useUIStore';
import { Icon } from '@/components/ui/icon';
import { ConfirmationBanner } from '@/components/shared/booking/common/confirmation/banner';
import { useBookingShare } from '@/lib/core/utils/share';
import type { MewsConfirmationProps } from './types';
import { SUNBED_LABELS } from '../labels';

// ── MewsConfirmation ────────────────────────────────────────────────────────

export function MewsConfirmation({
  booking,
  reservation,
  onGoHome,
  onSeeBookingDetails,
  labels = SUNBED_LABELS,
  unitIcon = 'sea-and-sun',
  whenLabel,
}: MewsConfirmationProps) {
  const t = useClientTranslation();
  const { user } = useApp() as { user?: { name?: string | null } };
  const setMainClassName = useUIStore(s => s.setMainClassName);
  const { handleShare } = useBookingShare({
    publicGuid: reservation?.publicGuid,
    name: booking?.venue?.name,
    venue: booking?.venue,
  });

  useEffect(() => {
    setMainClassName('pb-0!');
    return () => setMainClassName('');
  }, [setMainClassName]);

  const firstName = getFirstName(user?.name) ?? null;

  const venueImage = ImageMap(booking.venue?.image as ImageWithCrops, 'bookingDetailHero');
  const venueLogo = ImageMap(booking.venue?.logo as ImageWithCrops);

  const totalUnits = booking.allocations.reduce((s, a) => s + a.quantity, 0);
  const totalGuests = booking.adults + booking.children;
  const totalExtras = booking.extras.length;

  const dateDisplay = booking.dateFrom ? formatShortDate(booking.dateFrom) : '';
  // An hourly booking is one time unit, so the caller hands over a day-and-hour label instead: the
  // bare date would drop the hour the guest actually chose.
  const dateRange =
    whenLabel ??
    (booking.dateFrom && booking.dateTo && booking.dateFrom.toDateString() !== booking.dateTo.toDateString()
      ? `${formatShortDate(booking.dateFrom)} – ${formatShortDate(booking.dateTo)}`
      : dateDisplay);

  return (
    <ConfirmationBanner
      image={venueImage}
      logo={venueLogo}
      firstName={firstName}
      title={t(labels.bookingConfirmed)}
      rows={[
        [
          ...(dateRange ? [{ text: dateRange }] : []),
          ...(totalUnits > 0
            ? [
                {
                  icon: unitIcon,
                  text: `${totalUnits} ${t(labels.unit, { count: totalUnits })}`,
                },
              ]
            : []),
        ],
        [
          ...(totalExtras > 0
            ? [
                {
                  icon: 'hexagon-plus',
                  text: `${totalExtras} ${totalExtras === 1 ? t(labels.addOn) : t(labels.addOns)}`,
                },
              ]
            : []),
          {
            icon: 'group',
            text: `${totalGuests} ${totalGuests === 1 ? t('App.Labels.Guest') : t('App.Labels.Guests')}`,
          },
        ],
      ]}
      actions={[
        {
          label: t('App.Labels.GoBackHome'),
          onClick: onGoHome,
          variant: 'secondary-outline',
        },
        {
          label: t('App.Labels.SeeBookingDetails'),
          onClick: onSeeBookingDetails,
          variant: 'primary-solid',
        },
      ]}
      topPanel={
        <div className="pt-safe-area flex items-center justify-end px-4">
          <button
            type="button"
            className="flex size-10 items-center justify-center"
            aria-label={t('App.Labels.ShareGuests')}
            onClick={handleShare}
          >
            <Icon name="ShareIos" width={24} height={24} strokeWidth={1.5} className="text-white" />
          </button>
        </div>
      }
      className="-mt-[max(1rem,env(safe-area-inset-top))]!"
    />
  );
}
